<template>
    <div v-if="enabled" class="checkout-addons-header" :data-step-valid="!hasFormErrors">
        <p class="checkout-addons-title">
            {{ t({ key: 'service_package.select_service_package' }) }}
        </p>
    </div>
    <Box
        v-if="enabled"
        :radius="isTablet ? 's' : 'none'"
        :padding="isLargeDesktop ? 'm' : 's'"
        :overflowHidden="true">
        <div class="service-package-section">
            <Badge
                v-if="showValidationBadge"
                :text="t({ key: 'validation.defined' })"
                class="service-package-section-error"
                size="m"
                color="red"
                iconPosition="right" />

            <FormV2
                id="form_service_package"
                class="service-package-section-form"
                :horizontal="isLargeDesktop"
                :onValidChange="handleValidChange"
                :validationSchema="validationSchema"
                :initialValues="initialValues"
                :onErrorChange="handleErrorChange">
                <Field
                    v-for="(addon, index) in sortedServicePackageAddons"
                    :key="index"
                    :name="addon.Id"
                    v-slot="{ errors }">
                    <ServicePackageCard
                        :modelValue="selectedAddonId === addon.Id"
                        @update:modelValue="handleAddonSelection(addon)"
                        :addon="addon"
                        :errors="errors" />
                </Field>
            </FormV2>
            <Link
                v-if="isLargeDesktop"
                :href="readMoreLink"
                :label="t({ key: 'shared.extended_more_info' })"
                target="_blank"
                class="service-package-section-link" />
            <WarningModal
                :visible="showWarning"
                :onAcceptLabel="t({ key: 'service_package.yes_change' })"
                :onSelect="(value) => (value ? acceptChange() : denyChange())">
                <ServicePackageWarning :addon="basicAddon" />
            </WarningModal>
        </div>
    </Box>
</template>

<script lang="ts">
    import { computed, defineComponent, ref, watch } from 'vue';
    import { useStore } from '@/store';
    import { t } from '@shared/helpers/translation';
    import { useScreenSize } from '@shared/helpers/viewport';
    import {
        ServicePackageAddon,
        SerializationType,
        ServicePackageType,
    } from '@/api/Cart/cart.interfaces';
    import { FormErrors, FormValues } from '@ui/components/FormV2/Form.interfaces';
    import { ValidationSchema } from '@ui/components/FormV2/Validations/Validations.interfaces';
    import { Field } from 'vee-validate';
    import { BASELINE_SERVICE_PACKAGE } from './ServicePackage.config';
    import { trackClarity } from '@/tracking/clarity';
    import { ClarityEvent } from '@/tracking/clarity/types';
    import {
        logStatsigCheckoutAddRemoveAddon,
        logStatsigCheckoutServicePackageWarningInteraction,
    } from '@/tracking/statsig/checkout.functions';
    import { logAnalyticsAddRemoveProduct } from '@/tracking/analytics/Checkout/Functions/checkout.functions';
    import { EventName } from '@/tracking/analytics/types';
    import { StatsigEvent } from '@/tracking/statsig/types';
    import Box from '@ui/components/Box/Box.vue';
    import FormV2 from '@ui/components/FormV2/Form.vue';
    import ServicePackageCard from './ServicePackageCard.vue';
    import Badge from '@ui/components/Badge/Badge.vue';
    import Link from '@ui/components/Link/Link.vue';
    import WarningModal from '@/components/shared/WarningModal.vue';
    import ServicePackageWarning from '@/components/shared/ServicePackageWarning/ServicePackageWarning.vue';

    export default defineComponent({
        name: 'ServicePackage',
        methods: { t },
        components: {
            Box,
            FormV2,
            Field,
            ServicePackageCard,
            Badge,
            Link,
            WarningModal,
            ServicePackageWarning,
        },
        props: {
            sectionId: {
                type: String,
            },
        },
        setup(props) {
            const store = useStore();
            const { isTablet, isLargeDesktop } = useScreenSize();

            const formErrors = ref<FormErrors>({});
            const showWarning = ref<boolean>(false);
            const selectedAddonId = ref<string | null>(null);

            const dynamicAddons = computed(
                () => store.getters['cart/getServicePackageAddons'] || []
            );

            const servicePackageAddons = computed((): ServicePackageAddon[] => {
                return [BASELINE_SERVICE_PACKAGE, ...dynamicAddons.value];
            });

            const selectedAddon = computed(() =>
                servicePackageAddons.value.find((addon) => addon.Id === selectedAddonId.value)
            );

            const basicAddon = computed(
                (): ServicePackageAddon =>
                    servicePackageAddons.value.find(
                        (addon) => addon.Ordinal === ServicePackageType.Basic
                    ) ?? ({} as ServicePackageAddon)
            );

            const enabled = computed(() => dynamicAddons.value.length > 0);

            const hasFormErrors = computed(() => Object.keys(formErrors.value).length > 0);

            const readMoreLink = computed(() => {
                const basicServicePackage = servicePackageAddons.value.find(
                    (addon) => addon.Ordinal === ServicePackageType.Basic
                );
                return basicServicePackage?.LinkUrl ?? '';
            });

            const sortedServicePackageAddons = computed(() => {
                return [...servicePackageAddons.value].sort((a, b) => {
                    const ordinalA = a.Ordinal || 0;
                    const ordinalB = b.Ordinal || 0;
                    return ordinalA - ordinalB;
                });
            });

            const validationSchema = computed((): ValidationSchema => {
                const schema: ValidationSchema = {};

                const requireOneSelection = () => {
                    return selectedAddonId.value !== null;
                };

                if (sortedServicePackageAddons.value.length > 0) {
                    schema[sortedServicePackageAddons.value[0].Id] = [requireOneSelection];
                }

                return schema;
            });

            const initialValues = computed(() => {
                const values: FormValues = {};

                const cartServicePackage = store.getters['cart/getProducts'].find(
                    (p: any) =>
                        p.Type === 'Addon' && p.$type === SerializationType.ServicePackage
                );

                sortedServicePackageAddons.value.forEach((addon) => {
                    const isInCart = cartServicePackage?.Id === addon.Id;
                    values[addon.Id] = isInCart;

                    if (isInCart) {
                        selectedAddonId.value = addon.Id;
                    }
                });

                return values;
            });

            const showValidationBadge = computed(() => {
                return hasFormErrors.value;
            });

            function setShowWarning(visible: boolean) {
                showWarning.value = visible;
            }

            function handleErrorChange(errors: FormErrors) {
                formErrors.value = errors;
            }

            function onSelectTracking(addon: ServicePackageAddon) {
                logAnalyticsAddRemoveProduct(addon, EventName.CHECKOUT_ADD_PRODUCT);
                logStatsigCheckoutAddRemoveAddon(addon, StatsigEvent.CHECKOUT_ADD_ADDON);
            }

            function onDeselectTracking(addon: ServicePackageAddon) {
                logAnalyticsAddRemoveProduct(addon, EventName.CHECKOUT_REMOVE_PRODUCT);
                logStatsigCheckoutAddRemoveAddon(addon, StatsigEvent.CHECKOUT_REMOVE_ADDON);
            }

            function handleValidChange(valid: boolean) {
                store.dispatch('checkout/setValid', {
                    id: props.sectionId,
                    valid,
                });
            }

            function handleAddonSelection(addon: ServicePackageAddon) {
                selectedAddonId.value = addon.Id;
                handleTouchedField(true, addon.Id);
            }

            function handleAddonPurchase(addon: ServicePackageAddon) {
                // SP already added in Products[]
                const cartServicePackage: ServicePackageAddon = store.getters[
                    'cart/getProducts'
                ].find(
                    (p: ServicePackageAddon) =>
                        p.Type === 'Addon' && p.$type === SerializationType.ServicePackage
                );

                // remove cart SP
                if (cartServicePackage) {
                    store.commit('cart/removeAddon', cartServicePackage);
                    onDeselectTracking(cartServicePackage);
                }

                // If SP basic or premium, we buy it
                if (addon.Ordinal !== ServicePackageType.None) {
                    store.commit('cart/buyAddon', addon);
                    onSelectTracking(addon);

                    if (addon.Ordinal === ServicePackageType.Premium) {
                        trackClarity(ClarityEvent.CHECKOUT_SERVICE_PACKAGE_PREMIUM_SELECTED);
                    }
                }
            }

            function handleTouchedField(value: boolean | undefined, name: string) {
                store.dispatch('checkout/setTouchedField', {
                    id: props.sectionId,
                    name,
                    value,
                });
            }

            function acceptChange() {
                selectedAddonId.value = basicAddon.value.Id;
                setShowWarning(false);
                logStatsigCheckoutServicePackageWarningInteraction('yes');
            }

            function denyChange() {
                setShowWarning(false);
                logStatsigCheckoutServicePackageWarningInteraction('no');
            }

            watch(selectedAddonId, (newValue) => {
                if (newValue === null) {
                    return;
                }

                formErrors.value = {};

                if (selectedAddonId.value && selectedAddon.value) {
                    const isNone = selectedAddon.value.Ordinal === ServicePackageType.None;
                    const basicAddonHasPrice =
                        basicAddon.value && basicAddon.value.Price.Amount > 0;

                    handleAddonPurchase(selectedAddon.value);

                    if (isNone && basicAddonHasPrice) {
                        setShowWarning(true);
                    }
                }
            });

            watch(showValidationBadge, (newValue, oldValue) => {
                if (newValue && !oldValue) {
                    trackClarity(ClarityEvent.CHECKOUT_SERVICE_PACKAGE_VALIDATION_ERROR);
                }
            });

            return {
                enabled,
                showWarning,
                hasFormErrors,
                isTablet,
                isLargeDesktop,
                sortedServicePackageAddons,
                selectedAddonId,
                initialValues,
                validationSchema,
                showValidationBadge,
                readMoreLink,
                handleValidChange,
                handleErrorChange,
                handleAddonSelection,
                acceptChange,
                denyChange,
                basicAddon,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward './ServicePackage.scss';
    @forward '../Addons/Addons.scss';
</style>
